<template>
    <div class="vx-p-6 vx-bg-slate-300/30 vx-rounded-2xl vx-shadow-inner vx-border-solid vx-border-b vx-border-t-0 vx-border-x-0 vx-border-white vx-gap-4
        vx-flex vx-flex-col sm:vx-grid sm:vx-grid-cols-12 xl:vx-flex xl:vx-flex-col">
        <div class="vx-col-span-12 sm:vx-hidden">
            <vx-button @click="showFilters = !showFilters" color="muted-dark" size="sm" block>
                <font-awesome-icon :icon="showFilters ? faCaretUp : faCaretDown"></font-awesome-icon>
                {{ showFilters ? 'Hide' : 'Show' }} Filters
            </vx-button>
        </div>
        <div v-if="showFilters" class="vx-col-span-4 lg:vx-col-span-3">
            <div class="vx-font-bold vx-text-xs vx-text-slate-700 vx-mb-2">Range</div>
            <vx-box color="info"
                class="vx-p-4 vx-rounded-b-none vx-border-b-0">
                <div class="vx-flex vx-flex-col vx-gap-3">
                    <div v-for="rangeOption in rangeOptions"
                        :key="rangeOption.value"
                        class="vx-flex vx-gap-2 vx-items-center vx-text-sm vx-cursor-pointer"
                        :data-test="`range-${rangeOption.value}`"
                        @click.prevent="handleDateRangeOption(rangeOption.value)"
                        @keydown.space.prevent="handleDateRangeOption(rangeOption.value)"
                        @keydown.enter.prevent="handleDateRangeOption(rangeOption.value)">
                        <vx-radio :modelValue="selectedRange.value === rangeOption.value"></vx-radio>
                        {{ rangeOption.label }}
                    </div>
                </div>
            </vx-box>

            <Transition enter-from-class="vx-opacity-0 -vx-translate-y-4"
                enter-to-class="vx-opacity-100 vx-translate-y-0"
                leave-from-class="vx-opacity-100 vx-translate-y-0"
                leave-to-class="vx-opacity-0 -vx-translate-y-4">
                <div v-if="isDatePickerOpened"
                    class="vx-absolute vx-z-20 vx-mb-8 vx-w-full vx-transition lg:vx-max-w-md"
                    @click.stop>
                    <vx-date-picker mode="date"
                        :modelValue="datePickerRangeModel"
                        @update:modelValue="handleDatePickerRange"
                        is-range
                        v-on-click-outside="() => closeDatePickerRange()"
                        :timezone="getClientTz()"
                        :maxDate="maxCustomDate"
                        :minDate="minCustomDate"
                        @dayclick="handleDayClick" />
                </div>
            </Transition>

            <div
                class="vx-flex vx-text-xs vx-text-black vx-rounded-b-2xl vx-border vx-border-solid vx-shadow vx-transition vx-border-white vx-border-t-slate-300/70">
                <button v-if="selectedRange.value !== 'selected_custom'"
                    @click.prevent="isPrevRangeAllowed && setPrevRange()"
                    :disabled="!isPrevRangeAllowed"
                    data-test="previous-period-btn"
                    class="vx-flex vx-flex-row vx-rounded-b-2xl vx-bg-slate-200 vx-flex-grow vx-gap-2 vx-px-4 vx-py-3 vx-items-center vx-font-semibold"
                    :class="{ 'hover:vx-bg-slate-300': isPrevRangeAllowed, 'vx-cursor-default': !isPrevRangeAllowed }">
                    <font-awesome-icon :icon="faArrowLeft"></font-awesome-icon>
                    Previous Period
                </button>

                <div v-else
                    class="vx-flex vx-flex-grow vx-items-center vx-justify-between">
                    <button @click.prevent="isPrevRangeAllowed && setPrevRange()"
                        data-test="prev-btn"
                        :disabled="!isPrevRangeAllowed"
                        class="vx-p-2 vx-flex-grow vx-flex vx-pl-4 vx-py-3 vx-rounded-bl-2xl vx-bg-slate-200 vx-flex-row vx-items-center vx-gap-2 vx-font-semibold"
                        :class="{ 'hover:vx-bg-slate-300': isPrevRangeAllowed, 'vx-cursor-default': !isPrevRangeAllowed }">
                        <font-awesome-icon :icon="faArrowLeft"></font-awesome-icon>
                        Prev
                    </button>
                    <button @click.prevent="isNextRangeAllowed && setNextRange()"
                        data-test="next-btn"
                        :disabled="!isNextRangeAllowed"
                        class="vx-justify-end vx-p-2 vx-flex-grow vx-flex vx-pr-4 vx-py-3 vx-rounded-br-2xl vx-bg-slate-200 vx-flex-row vx-items-center vx-gap-2 vx-font-semibold"
                        :class="{ 'hover:vx-bg-slate-300': isNextRangeAllowed, 'vx-cursor-default': !isNextRangeAllowed }">
                        Next
                        <font-awesome-icon :icon="faArrowRight"></font-awesome-icon>
                    </button>
                </div>
            </div>
        </div>
        <div v-if="showFilters" class="vx-col-span-4 lg:vx-col-span-5">
            <div class="vx-font-bold vx-text-xs vx-text-slate-700 vx-mb-2">Resource Type</div>
            <vx-box color="info"
                class="vx-p-4">
                <div class="vx-flex vx-flex-col vx-gap-3">
                    <div v-for="resourceTypeOption in resourceTypesOptions"
                        :key="resourceTypeOption.value"
                        class="vx-flex vx-gap-2 vx-items-center vx-text-sm vx-cursor-pointer"
                        :data-test="`resource-type-${resourceTypeOption.value}`"
                        @click.prevent="setResourceType(resourceTypeOption.value)"
                        @keydown.space.prevent="setResourceType(resourceTypeOption.value)"
                        @keydown.enter.prevent="setResourceType(resourceTypeOption.value)">
                        <vx-radio :modelValue="resourceTypeRadioValue(resourceTypeOption.value)"></vx-radio>
                        {{ resourceTypeOption.label }}
                    </div>
                </div>
            </vx-box>
        </div>
        <div v-if="showFilters" class="vx-col-span-4 lg:vx-col-span-4">
            <div class="vx-font-bold vx-text-xs vx-text-slate-700 vx-mb-2">Audience Type</div>
            <vx-box color="info"
                class="vx-p-4">
                <div class="vx-flex vx-flex-col vx-gap-3">
                    <div v-for="audienceTypeOption in audienceTypesOptionsList"
                        :key="audienceTypeOption.name"
                        class="vx-flex vx-gap-2 vx-items-center vx-text-sm vx-cursor-pointer"
                        :class="{
                            'vx-opacity-70 !vx-cursor-not-allowed': audienceTypes.length === 1 && audienceTypes.includes(audienceTypeOption.name),
                        }"
                        :data-test="`audience-type-${audienceTypeOption.name}`"
                        @click.prevent="toggleAudienceType(audienceTypeOption.name)"
                        @keydown.space.prevent="toggleAudienceType(audienceTypeOption.name)"
                        @keydown.enter.prevent="toggleAudienceType(audienceTypeOption.name)">
                        <vx-checkbox :modelValue="audienceTypes.includes(audienceTypeOption.name)"></vx-checkbox>
                        {{ audienceTypeOption.label }}
                    </div>
                </div>
            </vx-box>
        </div>
        <div v-if="showFilters" class="vx-col-span-12">
            <div class="vx-font-bold vx-text-xs vx-text-slate-700 vx-mb-2">Resource</div>
            <vx-selectable v-model="resource"
                placeholder="-"
                :loading="loadingResources"
                :options="resourcesOptions"
                :reduce="option => ({ resource_type: option.resource_type, resource_id: option.resource_id })"
                label="resource_name"
                @search="searchResource"
                :clearable="false"
                data-test="resource-selectable">

                <template v-slot:option="{ option }">
                    <font-awesome-icon v-if="isMixedResource"
                        fixed-width
                        class="vx-px-1.5 vx-mr-2 vx-text-slate-500 vx-text-xs"
                        :icon="resourceIcon(option.resource_type)"></font-awesome-icon>
                    {{ option.resource_name }}
                </template>
            </vx-selectable>
        </div>
    </div>
</template>

<script setup>
import { VxBox, VxButton, VxCheckbox, VxDatePicker, VxRadio, VxSelectable } from '@voxie/frontend-components';
import { vOnClickOutside } from '@vueuse/components'
import { computed, nextTick, ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';
import { getDateRange, formatDateForRange, getCurrentDate, minAllowedDate } from '~/utils/analytics';
import { getClientTz, shortDate } from '~/utils/date';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft, faArrowRight, faCaretDown, faCaretUp, faFolderGear, faMessages, faRobot, faUser } from '@fortawesome/pro-solid-svg-icons';
import { faNotebook } from '@fortawesome/pro-regular-svg-icons';
import { analyticsClient } from '~/services';
import { audienceTypes as audienceTypesOptions } from '~/constants/campaign';

const toasted = useToasted();

const teamId = Spark.state.currentTeam.id;

const fetchParams = defineModel({
    type: Object,
    default: () => ({
        range: [],
        filters: {
            audience_types: audienceTypesOptions().map(audienceType => audienceType.name)
        },
    }),
});

const showFilters = ref(true);

const resource = computed({
    set: (resource) => {
        fetchParams.value.filters.resources = resource ? [resource] : undefined;
    },
    get: () => fetchParams.value.filters.resources?.[0]
});

const resourceTypes = computed({
    set: (resourceTypes) => {
        fetchParams.value.filters.resource_types = Array.isArray(resourceTypes) ? resourceTypes : undefined;
    },
    get: () => fetchParams.value.filters.resource_types || []
})
const audienceTypes = computed({
    set: (audienceTypes) => {
        fetchParams.value.filters.audience_types = audienceTypes.length ? audienceTypes : undefined;
    },
    get: () => fetchParams.value.filters.audience_types || []
})

const defaultRangeOptions = [
    { label: 'Last 7 Days', value: 'last_7_days', ...getDateRange('last_7_days') },
    { label: 'Last 14 Days', value: 'last_14_days', ...getDateRange('last_14_days') },
    { label: 'Last 30 Days', value: 'last_30_days', ...getDateRange('last_30_days') },
    { label: 'Current Month', value: 'month_today', ...getDateRange('month_today') },
];

const resourceTypesOptions = [
    { label: 'All', value: undefined },
    { label: 'Message Hub', value: 'user' },
    { label: 'Campaign', value: 'campaign' },
    { label: 'Automation', value: 'automation' },
    { label: 'Playbook', value: 'playbook' },
    { label: 'Other', value: 'other' },
];

const audienceTypesOptionsList = audienceTypesOptions();

const resourcesOptions = ref([]);
const loadingResources = ref();

const isMixedResource = computed(() => {
    const groupedByType = Object.values(
        resourcesOptions.value.reduce((acc, item) => {
            if (!acc[item.resource_type]) acc[item.resource_type] = [];
            acc[item.resource_type].push(item);

            return acc;
        }, {})
    );

    return groupedByType.length > 1;
});

const resourceTypeRadioValue = (resourceType) => {
    return resourceTypes.value.includes(resourceType) ||
        (resourceType === undefined && resourceTypes.value.length === 0);
};

const setResourceType = (resourceType) => {
    resourceTypes.value = resourceType ? [resourceType] : undefined;
};

const resourceIcon = (type) => {
    switch (type) {
        case 'user':
            return faUser;
        case 'campaign':
            return faMessages;
        case 'automation':
            return faRobot;
        case 'playbook':
            return faNotebook
        default:
            return faFolderGear;
    }
};

const toggleAudienceType = (audienceType) => {
    if (audienceTypes.value.includes(audienceType)) {
        if (audienceTypes.value.length === 1) {
            return;
        }
        audienceTypes.value = audienceTypes.value.filter((type) => type !== audienceType);
    } else {
        audienceTypes.value = [...audienceTypes.value, audienceType];
    }
};

const searchResource = debounce((query) => {
    loadingResources.value = true;

    analyticsClient.outboundMessagesListActiveResources(teamId, {
        ...fetchParams.value,
        query: query,
    }).then((response) => {
        resourcesOptions.value = response.data.data;
    }).catch((e) => {
        console.error(e);
        toasted.global.platform_error();
    }).finally(() => {
        loadingResources.value = false;
    });
}, 300);

// dayjs calculates an end date starting from a next day. in order to get the right range including a current day we decrease max days by 1.
const MAX_DAYS_RANGE = 89;

const clickedDay = ref(null);

const handleDayClick = (event) => {
    if (!clickedDay.value) {
        clickedDay.value = event.date;
    }
};

const maxAllowed = computed(() => {
    const currentDate = getCurrentDate();

    // current day is only available if the time is at least 01:30 local.
    if (currentDate.isAfter(currentDate.clone().set('hour', 1).set('minutes', 30))) {
        return currentDate.toDate();
    }

    return currentDate.subtract(1, 'day').endOf('day').toDate();
});

const maxCustomDate = computed(() => {
    if (clickedDay.value && dayjs(clickedDay.value).add(MAX_DAYS_RANGE, 'days').isAfter(maxAllowed.value)) {
        return maxAllowed.value;
    }

    if (clickedDay.value) {
        return dayjs(clickedDay.value).add(MAX_DAYS_RANGE, 'days').toDate();
    }

    return maxAllowed.value;
});

const minCustomDate = computed(() => {
    if (clickedDay.value && dayjs(clickedDay.value).subtract(MAX_DAYS_RANGE, 'days').isBefore(minAllowedDate)) {
        return minAllowedDate.toDate();
    }

    if (clickedDay.value) {
        return dayjs(clickedDay.value).subtract(MAX_DAYS_RANGE, 'days').toDate();
    }

    return minAllowedDate.toDate();
});

const defaultCustomRange = () => {
    return { label: 'Custom', value: 'custom' };
};
const customRangeOption = ref(defaultCustomRange());
const rangeOptions = computed(() => {
    return defaultRangeOptions.concat([{ ...customRangeOption.value }]);
});

// In order to get fresh dates on reset.
const defaultDatePickerRange = () => ({
    start: dayjs(maxCustomDate.value).tz(getClientTz()).subtract(2, 'days').startOf('day').toDate(),
    end: dayjs(maxCustomDate.value).tz(getClientTz()).startOf('day').toDate(),
});

const selectedRange = ref({ label: 'Last 30 Days', value: 'last_30_days', ...getDateRange('last_30_days') });

const isDatePickerOpened = ref(false);
const datePickerRangeModel = ref(defaultDatePickerRange());

const isNextRangeAllowed = computed(() => {
    const interval = getInterval();
    const end = dayjs(selectedRange.value.end_date).tz(getClientTz()).add(interval, 'days');

    return !end.isAfter(maxAllowed.value, 'day');
});

const isPrevRangeAllowed = computed(() => {
    const interval = getInterval();
    const start = dayjs(selectedRange.value.start_date).tz(getClientTz()).add(-interval, 'days');

    return !start.isBefore(minAllowedDate, 'day');
});

const handleDateRangeOption = (optionKey) => {
    const option = rangeOptions.value.find((o) => o.value === optionKey);

    // This is possible when the user clicks outside datepicker on 'custom' option,
    // so handleDateRangeOption() and closeDatePickerRange() run at the same time and can cause not selected radio
    if (option !== undefined) {
        selectedRange.value = { ...option };
    }

    if (optionKey === 'custom' || optionKey === 'selected_custom') {
        isDatePickerOpened.value = true;
    } else if (!rangeOptions.value.find((o) => o.value === 'custom')) {
        // Same issue as above possible without this else if condition
        customRangeOption.value = defaultCustomRange();
    }
};

const getInterval = () => {
    return dayjs(selectedRange.value.end_date).tz(getClientTz()).add(1, 'day').diff(selectedRange.value.start_date, 'days');
};

const setCustomRangeOption = (start, end) => {
    customRangeOption.value = {
        value: 'selected_custom',
        start_date: formatDateForRange(start.tz(getClientTz())),
        end_date: formatDateForRange(end.tz(getClientTz())),
        label: `${shortDate(start)} - ${shortDate(end)}`,
    };

    selectedRange.value = customRangeOption.value;

    datePickerRangeModel.value = {
        start: start.tz(getClientTz()).startOf('day').toDate(),
        end: end.tz(getClientTz()).endOf('day').toDate(),
    };
};

const handleDatePickerRange = async ({ start, end }) => {
    selectedRange.value = customRangeOption.value;

    setCustomRangeOption(dayjs(start), dayjs(end));

    isDatePickerOpened.value = false;
    await nextTick();
    clickedDay.value = null;
};

const closeDatePickerRange = () => {
    isDatePickerOpened.value = false;

    if (selectedRange.value.value === 'custom') {
        handleDatePickerRange(defaultDatePickerRange());
    }
};

const setPrevRange = () => {
    const interval = getInterval();
    const start = dayjs(selectedRange.value.start_date).tz(getClientTz()).add(-interval, 'days');
    const end = dayjs(selectedRange.value.end_date).tz(getClientTz()).add(-interval, 'days');

    setCustomRangeOption(start, end);
};
const setNextRange = () => {
    const interval = getInterval();
    const start = dayjs(selectedRange.value.start_date).tz(getClientTz()).add(interval, 'days');
    const end = dayjs(selectedRange.value.end_date).tz(getClientTz()).add(interval, 'days');

    setCustomRangeOption(start, end);
};

watch(selectedRange, (newRange, oldRange) => {
    fetchParams.value.range = [selectedRange.value.start_date, selectedRange.value.end_date];
    if (selectedRange.value.value === 'selected_custom') {
        customRangeOption.value = selectedRange.value;
    }

    // reset datepicker range
    if (oldRange?.value === 'selected_custom' && newRange?.value !== 'selected_custom') {
        datePickerRangeModel.value = defaultDatePickerRange();
    }
}, { immediate: true });
</script>
